
import React, { useState } from 'react';
import { Category, Puzzle, UserProgress, User } from '../types';
import { ArrowLeft, CheckCircle2, PlayCircle, Lock, Swords, Sparkles, BookOpen, Eye, Heart } from 'lucide-react';
// Fix: Import SupportModal from Layouts instead of the decommissioned file
import { SupportModal, ParentalGate } from './Layouts';
import ImagePreviewModal from './ImagePreviewModal';

interface PuzzleCatalogueProps {
  category: Category;
  onSelect: (p: Puzzle) => void;
  onChallenge: (p: Puzzle) => void;
  onReadStory: (p: Puzzle) => void;
  onBack: () => void;
  onOpenSupport: () => void;
  progress: UserProgress;
  user: User | null;
  onToggleSponsorship: (status: boolean) => void;
  triggerOnlineAction?: (featureName: string, action: () => void) => void;
}

const PuzzleCatalogue: React.FC<PuzzleCatalogueProps> = ({ 
  category, 
  onSelect, 
  onChallenge, 
  onReadStory, 
  onBack, 
  onOpenSupport, 
  progress, 
  user, 
  onToggleSponsorship,
  triggerOnlineAction
}) => {
  const [previewPuzzle, setPreviewPuzzle] = useState<Puzzle | null>(null);
  const [isParentGateOpen, setIsParentGateOpen] = useState(false);

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-slate-50 pt-safe pb-safe px-safe">
      <div className="max-w-5xl mx-auto w-full">
        <button 
          onClick={onBack}
          className="mb-8 flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 transition-colors group px-2 py-1"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Catalogue
        </button>

        <header className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-cinzel font-bold text-slate-900">{category.name}</h1>
            <p className="text-slate-500 mt-2 font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              Select an option to begin
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full border border-white/50 shadow-sm">
               <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{category.puzzles.length} Available</span>
            </div>
            <button
              onClick={() => setIsParentGateOpen(true)}
              className="p-2.5 bg-rose-50 text-rose-500 hover:bg-rose-100 rounded-full border border-rose-100/50 transition-colors active:scale-95 shadow-sm cursor-pointer flex items-center justify-center"
              title="Unlock & Support Options"
            >
              <Heart className="w-4 h-4 fill-rose-500 animate-pulse" />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pb-12">
          {category.puzzles.map((puzzle, index) => {
            const isSolved = progress.solvedPuzzles.includes(puzzle.id);
            const isStarted = !!progress.inProgress[puzzle.id];
            
            const isFreePuzzle = user?.isGuest ? index < 1 : index < 2;
            const isLocked = !progress.isSponsored && !isFreePuzzle;

            return (
              <div 
                key={puzzle.id}
                onClick={isLocked ? onOpenSupport : undefined}
                className={`group bg-white rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-sm transition-all relative
                  ${isLocked ? 'cursor-pointer hover:border-rose-200' : 'hover:shadow-2xl hover:border-blue-100'}
                `}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={puzzle.thumbnailUrl} 
                    alt={puzzle.title}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110
                      ${isLocked ? 'grayscale opacity-50 blur-[2px]' : ''}
                    `}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://picsum.photos/seed/${puzzle.id}/600/400`;
                    }}
                  />
                  
                  {isLocked && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[3px] text-white">
                      <div className="bg-white/20 p-5 rounded-full backdrop-blur-md border border-white/40 mb-3 shadow-2xl">
                         <Lock className="w-8 h-8 text-white" />
                      </div>
                      <span className="font-bold uppercase tracking-[0.2em] text-[10px] bg-black/40 px-4 py-2 rounded-full">Unlock with Sponsorship</span>
                    </div>
                  )}
                  
                  {isSolved && !isLocked && (
                    <div className="absolute top-6 right-6 bg-green-500 text-white p-2.5 rounded-full shadow-lg border-2 border-white z-10 animate-in zoom-in">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                  )}

                  <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity ${isLocked ? 'opacity-0' : 'opacity-100'}`}></div>

                  <div className={`absolute bottom-6 left-6 right-6 text-white transition-opacity ${isLocked ? 'opacity-0' : 'opacity-100'}`}>
                    <h3 className="text-2xl font-bold mb-1 leading-tight">{puzzle.title}</h3>
                    <p className="text-xs opacity-90 line-clamp-1 italic font-medium tracking-wide">{puzzle.description}</p>
                  </div>
                </div>

                {!isLocked && (
                  <div className="p-6 flex flex-col gap-3">
                    <div className="flex gap-2">
                       <button 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (triggerOnlineAction) {
                            triggerOnlineAction('Faith Duels / Custom Challenges', () => onChallenge(puzzle));
                          } else {
                            onChallenge(puzzle);
                          }
                        }}
                        className={`flex-1 p-4 rounded-2xl transition active:scale-95 border flex flex-col items-center justify-center gap-1 cursor-pointer ${!progress.isSponsored ? 'bg-gray-50 text-gray-300 border-gray-100' : 'bg-slate-50 text-slate-700 border-slate-100 hover:bg-slate-100'}`}
                        title="Create Faith Duel"
                      >
                        <Swords className="w-6 h-6" />
                        <span className="text-[8px] font-bold uppercase tracking-widest">Create Faith Duel</span>
                      </button>

                      <button 
                        onClick={(e) => { e.stopPropagation(); onReadStory(puzzle); }}
                        className="flex-1 p-4 rounded-2xl bg-slate-50 text-slate-700 border border-slate-100 hover:bg-slate-100 transition active:scale-95 flex flex-col items-center justify-center gap-1"
                      >
                         <BookOpen className="w-6 h-6" />
                         <span className="text-[8px] font-bold uppercase tracking-widest">Read/Listen</span>
                      </button>

                      <button 
                        onClick={(e) => { e.stopPropagation(); setPreviewPuzzle(puzzle); }}
                        className="flex-1 p-4 rounded-2xl bg-slate-50 text-slate-700 border border-slate-100 hover:bg-slate-100 transition active:scale-95 flex flex-col items-center justify-center gap-1"
                      >
                         <Eye className="w-6 h-6" />
                         <span className="text-[8px] font-bold uppercase tracking-widest">See Image</span>
                      </button>
                    </div>
                    
                    <button 
                      onClick={(e) => { e.stopPropagation(); onSelect(puzzle); }}
                      className="w-full flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white py-4 rounded-2xl font-bold transition-all shadow-lg active:scale-95"
                    >
                      <PlayCircle className="w-5 h-5" />
                      {isSolved ? 'Replay' : isStarted ? 'Resume' : 'Play Puzzle'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ParentalGate 
        isOpen={isParentGateOpen}
        onClose={() => setIsParentGateOpen(false)}
        onSuccess={() => {
          setIsParentGateOpen(false);
          onOpenSupport();
        }}
      />

      <ImagePreviewModal 
        isOpen={!!previewPuzzle}
        onClose={() => setPreviewPuzzle(null)}
        imageUrl={previewPuzzle?.imageUrl || ''}
        title={previewPuzzle?.title || ''}
      />
    </div>
  );
};

export default PuzzleCatalogue;
